// frontend/src/App.js

import React, { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  HashRouter,
  BrowserRouter,
  Routes,
  Route,
  Navigate
} from 'react-router-dom';

import Sidebar from './components/Sidebar/Sidebar';
import Dashboard from './components/Dashboard/Dashboard';
import Login from './components/Login/Login';
import JournalTemplateList from './components/JournalTemplateList/JournalTemplateList';
import JournalTemplateForm from './components/JournalTemplateForm/JournalTemplateForm';
import PrivateRoute from './components/PrivateRoute/PrivateRoute';
import UserManagement from './components/UserManagement/UserManagement';
import Clinics from './components/Clinics/Clinics';
import { KallelseTemplateList, KallelseTemplateForm } from './components/KallelseTemplate';
import { FormTemplateList, FormTemplateForm } from './components/FormTemplates';
import { DocumentTemplateList, DocumentTemplateForm } from './components/DocumentTemplates';
import SMSTemplateList from './components/SMSTemplate/SMSTemplateList';
import SMSTemplateForm from './components/SMSTemplate/SMSTemplateForm';
import Calendar from './components/Calendar';
import { AppointmentTypeManager, DailyCashReport, ProductManager, ProductSalesReport, GroupsView, ScheduleTemplateManager, ScheduleAssignmentManager, AbsenceOverview } from './components/Calendar';
import { PatientList, PatientView } from './components/PatientList';
import CalendarSettings from './components/CalendarSettings/CalendarSettings';

import { ToastContainer } from 'react-toastify';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-toastify/dist/ReactToastify.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './styles/theme.css';
import './styles/animations.css';
import './styles/buttons.css';
import './styles/modals.css';
import './styles/forms.css';
import './App.css';
import './cache-breaker-fix.css'; /* Cache breaker v1 - 2024-06-25 */

import { UserProvider, UserContext } from './UserContext';
import { RecordingProvider } from './recording/RecordingContext';
import GlobalRecordingBar from './recording/GlobalRecordingBar';
import { ActivePatientProvider } from './patient/ActivePatientContext';
import ActivePatientWidget from './patient/ActivePatientWidget';

/* ------------------------------------------------------------- */
/* Dynamiskt val mellan HashRouter (Electron) och BrowserRouter  */
/* ------------------------------------------------------------- */
const isElectron =
  typeof window !== 'undefined' &&
  window.process &&
  window.process.versions &&
  !!window.process.versions.electron;

const isFileProtocol = typeof window !== 'undefined' && window.location.protocol === 'file:';

const Router = (isElectron || isFileProtocol) ? HashRouter : BrowserRouter;
/* ------------------------------------------------------------- */

function AppContent() {
  const { user, loading } = useContext(UserContext);
  const navigate = useNavigate();

  useEffect(() => {
      if (!window.aionDesktop?.isElectron) return;

      const system = user?.clinic?.journal_system;
      if (system === 'PMO' || system === 'TakeCare') {
        window.aionDesktop.startTransfer(system);
        console.log(`Startade överföringsserver för ${system}`);
      } else {
        // inget journalsystem vi stöder => säkerställ att ingen EXE kör
        window.aionDesktop.stopTransfer();
      }

      // 🔙 cleanup – körs när komponenten avmonteras eller när user ändras
      return () => {
        if (window.aionDesktop?.isElectron) {
          window.aionDesktop.stopTransfer();
        }
      };
    }, [user]);

  if (loading) {
    return <div>Laddar...</div>;
  }

  return (
  <RecordingProvider>
    <ActivePatientProvider>
    <div className="app-container">
      {user && <Sidebar />}
      <div className="main-content">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/journalmallar" element={<PrivateRoute><JournalTemplateList /></PrivateRoute>} />
          <Route path="/journalmallar/create" element={<PrivateRoute><JournalTemplateForm /></PrivateRoute>} />
          <Route path="/journalmallar/edit/:id" element={<PrivateRoute><JournalTemplateForm /></PrivateRoute>} />
          {/* 📄 DOKUMENTMALLAR */}
          {/* KALLELSEMALLAR */}
          <Route path="/kallelsemallar" element={<PrivateRoute><KallelseTemplateList /></PrivateRoute>} />
          <Route path="/kallelsemallar/create" element={<PrivateRoute><KallelseTemplateForm /></PrivateRoute>} />
          <Route path="/kallelsemallar/edit/:id" element={<PrivateRoute><KallelseTemplateForm /></PrivateRoute>} />

          {/* Formulärmallar (patientformulär via Bokningsappen) — klinik-admin */}
          <Route path="/formularmallar" element={<PrivateRoute><FormTemplateList /></PrivateRoute>} />
          <Route path="/formularmallar/create" element={<PrivateRoute><FormTemplateForm /></PrivateRoute>} />
          <Route path="/formularmallar/edit/:id" element={<PrivateRoute><FormTemplateForm /></PrivateRoute>} />
          {/* Dokumentmallar (Spår D) */}
          <Route path="/dokumentmallar" element={<PrivateRoute><DocumentTemplateList /></PrivateRoute>} />
          <Route path="/dokumentmallar/create" element={<PrivateRoute><DocumentTemplateForm /></PrivateRoute>} />
          <Route path="/dokumentmallar/edit/:id" element={<PrivateRoute><DocumentTemplateForm /></PrivateRoute>} />
          {/* SMS-MALLAR */}
          <Route path="/sms-mallar" element={<PrivateRoute><SMSTemplateList /></PrivateRoute>} />
          <Route path="/sms-mallar/create" element={<PrivateRoute><SMSTemplateForm /></PrivateRoute>} />
          <Route path="/sms-mallar/edit/:id" element={<PrivateRoute><SMSTemplateForm /></PrivateRoute>} />
          <Route path="/users" element={<PrivateRoute><UserManagement /></PrivateRoute>} />
          <Route path="/clinics" element={<PrivateRoute><Clinics /></PrivateRoute>} />
          <Route path="/calendar" element={<PrivateRoute><Calendar /></PrivateRoute>} />
          <Route path="/calendar-settings" element={<PrivateRoute><CalendarSettings /></PrivateRoute>} />
          <Route path="/appointment-types" element={<PrivateRoute><AppointmentTypeManager /></PrivateRoute>} />
          <Route path="/daily-cash-report" element={<PrivateRoute><DailyCashReport /></PrivateRoute>} />
          <Route path="/product-statistics" element={<PrivateRoute><ProductSalesReport /></PrivateRoute>} />
          <Route path="/products" element={<PrivateRoute><ProductManager /></PrivateRoute>} />
          {/* Spår P: Grupper flyttad till huvudsidomenyn; gamla adressen pekas om */}
          <Route path="/groups" element={<PrivateRoute><GroupsView /></PrivateRoute>} />
          <Route path="/group-templates" element={<Navigate to="/groups" replace />} />
          <Route path="/schedule-templates" element={<PrivateRoute><ScheduleTemplateManager /></PrivateRoute>} />
          <Route path="/schedule-assignments" element={<PrivateRoute><ScheduleAssignmentManager /></PrivateRoute>} />
          {/* Admin → Frånvaro (Spår K2.4); sidan nekar själv icke-admin */}
          <Route path="/admin/franvaro" element={<PrivateRoute><AbsenceOverview /></PrivateRoute>} />
          <Route path="/patients" element={<PrivateRoute><PatientList /></PrivateRoute>} />
          <Route path="/patients/:patientId" element={<PrivateRoute><PatientView /></PrivateRoute>} />
          <Route path="/" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </div>
    <ToastContainer position="bottom-right" autoClose={3000} />
    {user && <GlobalRecordingBar />}
    {user && <ActivePatientWidget />}
    </ActivePatientProvider>
  </RecordingProvider>
  );
}

function App() {
  return (
    <UserProvider>
      <Router>
        <AppContent />
      </Router>
    </UserProvider>
  );
}

export default App;
